<template>
  <div class="carousel">
    <v-carousel
      :value="slide"
      height
      :continuous="false"
      :interval="interval"
      hide-delimiters
      show-arrows-on-hover
      :show-arrows="!isMobile"
      :class="{ 'listing-view-carousel': isListingView }"
      @change="$emit('update:slide', $event)"
    >
      <v-carousel-item v-for="item in items" :key="item.id" :eager="loadAll">
        <site-carousel-item
          class="rounded-lg"
          :item="item"
          :easy="easy"
          :easy-contain="easyContain"
          :tile="tile"
          :listing-id="listingId"
          :item-height="itemHeight"
          eager
        />
      </v-carousel-item>
    </v-carousel>
    <div
      v-if="showDots"
      :class="[
        'carousel-dots',
        { 'carousel-dots-easy': easy },
        'd-flex',
        'justify-center',
      ]"
    >
      <button
        v-for="d in getVisibleDots"
        :key="`dot-${d.index}`"
        :class="[
          {
            active: d.active,
            'more-indicator': d.isMoreIndicator,
          },
          'carousel-dots-item',
        ]"
        @click="d.isMoreIndicator ? null : $emit('update:slide', d.index)"
      />
    </div>
  </div>
</template>

<script>
import SiteCarouselItem from "@/components/common/carousel/site-carousel-item.vue";
import DeviceMixin from "@/mixins/device-mixin";
export default {
  name: "site-carousel",
  mixins: [DeviceMixin],
  components: { SiteCarouselItem },
  props: {
    easy: {
      type: Boolean,
      default: false,
    },
    easyContain: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
    },
    slide: {
      type: Number,
      default: 0,
    },
    showDots: {
      type: Boolean,
      default: true,
    },
    interval: {
      type: Number,
      default: 6000,
    },
    tile: {
      type: Boolean,
      default: false,
    },
    listingId: {
      type: [Number, String],
      default: null,
    },
    itemHeight: {
      type: String,
      default: null,
    },
    loadAll: {
      type: Boolean,
      default: false,
    },
    isListingView: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    viewDialog: false,
    touchStartX: null,
    touchStartY: null,
  }),
  mounted() {
    this.loadNextImage(this.slide);
    if (this.isMobile) {
      this.setupTouchHandling();
    }
  },
  beforeDestroy() {
    if (this.isMobile) {
      this.cleanupTouchHandling();
    }
  },
  watch: {
    slide(val) {
      this.loadNextImage(val);
    },
  },
  methods: {
    setupTouchHandling() {
      this.$el.addEventListener("touchstart", this.handleTouchStart, {
        passive: false,
      });
      this.$el.addEventListener("touchmove", this.handleTouchMove, {
        passive: false,
      });
    },
    cleanupTouchHandling() {
      this.$el.removeEventListener("touchstart", this.handleTouchStart);
      this.$el.removeEventListener("touchmove", this.handleTouchMove);
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    handleTouchMove(e) {
      if (!this.touchStartX || !this.touchStartY) return;

      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;

      const deltaX = Math.abs(touchEndX - this.touchStartX);
      const deltaY = Math.abs(touchEndY - this.touchStartY);

      // If horizontal swipe is more prominent than vertical, prevent default
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    },
    loadNextImage(val) {
      const nextIndex = (val + 1) % this.items.length;
      const nextImage = new Image();
      nextImage.src = this.items[nextIndex];
    },
    shouldShowDot(index) {
      if (this.items.length <= 5) return true;
      if (index < 5) return true;
      if (index === this.slide) return true;

      return false;
    },
  },
  computed: {
    getVisibleDots() {
      if (this.items.length <= 5) {
        return this.items.map((_, index) => ({
          index,
          active: this.slide === index,
          isMoreIndicator: false,
        }));
      }
      let dots = [];
      for (let i = 0; i < 4; i++) {
        dots.push({
          index: i,
          active: this.slide === i,
          isMoreIndicator: false,
        });
      }
      if (this.slide >= 4) {
        dots.push({
          index: this.slide,
          active: true,
          isMoreIndicator: false,
        });
      }
      dots.push({
        index: this.items.length - 1,
        active: false,
        isMoreIndicator: true,
      });

      return dots;
    },
  },
};
</script>

<style lang="scss">
.carousel {
  position: relative;
  touch-action: pan-y pinch-zoom;
  -webkit-overflow-scrolling: touch;
  &-dots {
    position: relative;
    padding-top: 40px;
    padding-bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    &-easy {
      position: absolute;
      padding-top: 15px;
      padding-bottom: 25px;
      bottom: 0;
      .carousel-dots-item {
        background-color: #fff;
      }
    }
    &-item {
      display: block;
      background-color: #000;
      margin-left: 4px;
      margin-right: 4px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      opacity: 0.3;
      &.active {
        opacity: 1;
      }
      &.more-indicator {
        width: 12px;
        border-radius: 3px;
      }
    }
  }
}
.v-window__next .v-btn--icon,
.v-window__prev .v-btn--icon {
  color: black !important;
  background: #ffffffb8 !important;
  width: 30px !important;
  height: 30px !important;
}
.v-window__next i,
.v-window__prev i {
  font-size: 28px !important;
}

.v-carousel {
  /* Allow both horizontal and vertical scrolling */
  touch-action: auto !important;
  -webkit-user-select: none;
  user-select: none;
}

.v-carousel__item {
  /* Allow both horizontal and vertical scrolling */
  touch-action: auto !important;
  -webkit-user-select: none;
  user-select: none;
}
.listing-view-carousel {
  .v-carousel__item {
    touch-action: pan-x !important;
  }
  .v-carousel {
    touch-action: pan-x !important;
  }
}
</style>
